/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { GuideProgram } from 'client-code/filtered-guide';
import { ScreenType } from 'client-code/previews';

import { DeviceModel } from '../../../core/device';
import { Channel } from '../../../core/entities';
import { InteractiveComponents } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/playback/enums';
import { SharedPrefConstants } from '../../../core/storage';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TChannelV2, TOfferPreview, TOfferPreviewCustomData } from '../../../core/types';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { showPCPinOverlayWithProgramRating } from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { hideOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import { getSingleChannelByChannelId } from '../../videoplayerui/onelineguide/onelineguide.reducers';
import { onSetFilteredGuideSelectedRowAction, setGhostModeAction } from './filteredguide.actions';
import {
    ON_FILTERED_GUIDE_API_ERROR,
    ON_REPAINT_FILTERED_GUIDE,
    ON_SET_CARET_VISIBILITY_FILTERED_GUIDE,
    ON_SET_FILTERED_GUIDE_SELECTED_ROW,
    ON_SET_FILTERED_GUIDE_SELECTED_ROW_PREVIEW,
    ON_SET_FILTERED_GUIDE_TURBO_SCROLL,
    ON_SET_GHOST_MODE_FILTERED_GUIDE,
    ON_SET_NULL_REFETCH_TIMESTAMP,
    ON_UPDATE_LAST_REFETCH_TIMESTAMP,
} from './filteredguide.types';
import {
    onSetHorizontalPositionFilteredGuideAction,
    onSetVerticalPositionFilteredGuideAction,
} from './navigation/filteredguide.navigation.actions';
import {
    ON_HORIZONTAL_KEY_PRESS_FILTERED_GUIDE,
    ON_SET_HORIZONTAL_POSITION_FILTERED_GUIDE,
    ON_SET_VERTICAL_POSITION_FILTERED_GUIDE,
    ON_VERTICAL_KEY_PRESS_FILTERED_GUIDE,
} from './navigation/filteredguide.navigation.types';

interface IFilteredGuideState {
    verticalPosition: number;
    horizontalPosition: number;
    selectedProgram: GuideProgram | null;
    ghostMode: boolean;
    isCaretVisible: boolean;
    selectedProgramPreviewPromise: Promise<TOfferPreview> | null;
    remountCounter: number; // Used to force filtered guide to remount
    filteredGuideTurboScroll: boolean;
    filteredGuideTurboScrollDirection: InteractiveComponents.NORTH | InteractiveComponents.SOUTH;
    lastRefetchTimestamp: number | null;
    isError: boolean;
}

const initialState: IFilteredGuideState = {
    verticalPosition: 0,
    horizontalPosition: 1,
    selectedProgram: null,
    ghostMode: false,
    isCaretVisible: false,
    selectedProgramPreviewPromise: null,
    remountCounter: 0,
    filteredGuideTurboScroll: false,
    filteredGuideTurboScrollDirection: InteractiveComponents.NORTH,
    lastRefetchTimestamp: null,
    isError: false,
};

const filteredGuideReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IFilteredGuideState>>{},
) => {
    switch (type) {
        case ON_VERTICAL_KEY_PRESS_FILTERED_GUIDE:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_HORIZONTAL_KEY_PRESS_FILTERED_GUIDE:
            return { ...state, horizontalPosition: payload.horizontalPosition };

        case ON_SET_FILTERED_GUIDE_SELECTED_ROW:
            return { ...state, selectedProgram: payload.selectedProgram };

        case ON_SET_VERTICAL_POSITION_FILTERED_GUIDE:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_SET_HORIZONTAL_POSITION_FILTERED_GUIDE:
            return { ...state, horizontalPosition: payload.horizontalPosition };

        case ON_SET_FILTERED_GUIDE_SELECTED_ROW_PREVIEW:
            return {
                ...state,
                selectedProgramPreviewPromise: payload.selectedProgramPreviewPromise,
            };
        case ON_REPAINT_FILTERED_GUIDE:
            return {
                ...state,
                remountCounter: state.remountCounter + 1,
            };
        case ON_SET_FILTERED_GUIDE_TURBO_SCROLL:
            return {
                ...state,
                filteredGuideTurboScroll: payload.filteredGuideTurboScroll,
                filteredGuideTurboScrollDirection: payload.filteredGuideTurboScrollDirection,
            };

        case ON_SET_GHOST_MODE_FILTERED_GUIDE:
            return {
                ...state,
                ghostMode: payload.ghostMode,
            };

        case ON_SET_CARET_VISIBILITY_FILTERED_GUIDE:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case ON_UPDATE_LAST_REFETCH_TIMESTAMP:
            return {
                ...state,
                lastRefetchTimestamp: new Date().getTime(),
            };
        case ON_SET_NULL_REFETCH_TIMESTAMP:
            return {
                ...state,
                lastRefetchTimestamp: null,
            };
        case ON_FILTERED_GUIDE_API_ERROR:
            return {
                ...state,
                isError: payload.isError,
            };
        default:
            return state;
    }
};

export default filteredGuideReducer;

export const setFilteredGuideSelectedProgram =
    (selectedProgram: GuideProgram | null) => (dispatch: Function) => {
        dispatch(onSetFilteredGuideSelectedRowAction(selectedProgram));
    };

export const disableGhostModeFilteredGuide = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(false));
};

export const enableGhostModeFilteredGuide = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(true));
};

export const resetFilteredGuideIndexes = () => (dispatch: Function) => {
    dispatch(onSetFilteredGuideSelectedRowAction(null));
    dispatch(onSetVerticalPositionFilteredGuideAction(0));
    dispatch(onSetHorizontalPositionFilteredGuideAction(1));
};

const getCurrentOfferPreview = async (
    program: GuideProgram,
    programPreviewPromise: Promise<TOfferPreview>,
): Promise<TOfferPreviewCustomData | null> => {
    try {
        const preview = await programPreviewPromise;
        return preview.offerPreview;
    } catch {
        try {
            const service = new ServicesClientImpl();
            const res = await service.fetchOfferPreview(program.offerId, ScreenType.GuideHeader);
            return res.offerPreview;
        } catch (error) {
            return null;
        }
    }
};

export const filteredGuideWatchNowLiveProgram =
    (channel: Channel | TChannelV2, isExplicitlyAdult: boolean, hideOverlay: boolean) =>
    async (dispatch: Function) => {
        const livePlayable: IPlayable = {
            channel,
            playbackContentType: PlaybackContentType.LIVE,
        };

        if (hideOverlay) {
            dispatch(hideOverlayDialog());
        }
        const services = new ServicesClientImpl();
        if (isExplicitlyAdult && services.shouldHideMetadata()) {
            dispatch(
                showPCPinOverlayWithProgramRating(
                    () => () => dispatch(startVideoPlayback(livePlayable)),
                    isExplicitlyAdult,
                ),
            );
        } else {
            dispatch(startVideoPlayback(livePlayable));
        }
    };

export const filteredGuideGetPreviewAndMetadata = async (
    filteredGuideReduxState: IFilteredGuideState,
    channelOptionsOverlayReduxState: { channelsType: string },
) => {
    const undefinedValues = {
        currentOfferPreview: undefined,
        isExplicitlyAdult: undefined,
        isContentPCViolated: undefined,
        channel: undefined,
    };
    const { selectedProgram, selectedProgramPreviewPromise } = filteredGuideReduxState;
    const { channelsType } = channelOptionsOverlayReduxState;

    if (!selectedProgram) {
        return undefinedValues;
    }

    const channel = await getSingleChannelByChannelId(
        selectedProgram.channelId || '',
        channelsType,
    );

    // Note: if the channel is not found, it may also mean that the user is trying to open
    // the overlay on a PPV/JUMP/4K channel, which is not supported.
    if (!channel || NetworkManager.isNetworkDisconnected()) {
        return undefinedValues;
    }

    if (!selectedProgramPreviewPromise) {
        return {
            ...undefinedValues,
            channel,
        };
    }
    const currentOfferPreview: TOfferPreviewCustomData | null = await getCurrentOfferPreview(
        selectedProgram,
        selectedProgramPreviewPromise,
    );

    if (!currentOfferPreview) {
        return {
            ...undefinedValues,
            channel,
        };
    }

    const isExplicitlyAdult = currentOfferPreview.contentPreview?.isAdult ?? false;
    const isContentPCViolated = new ServicesClientImpl().checkPCViolations({
        offerPreview: currentOfferPreview,
    });

    return {
        currentOfferPreview,
        isExplicitlyAdult,
        isContentPCViolated,
        channel,
    };
};

export const updateRecordingSharedPreferenceBeforeOpeningOverlay = (
    selectedProgram: GuideProgram,
) => {
    const recordingSharedPreferenceEditor = DeviceModel.getSharedPreferences(
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
    ).edit();

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_OFFER_ID,
        selectedProgram.offerId,
    );

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_CONTENT_ID,
        selectedProgram.contentId,
    );
    recordingSharedPreferenceEditor.commit();
};
